import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Container, Row, Col, Spinner, Tabs, Tab } from 'react-bootstrap';
import Filter from './components/filter/filter';
import CorporateActionPaymentReportContainer from './components/corporate-action-payment-report-container/corporate-action-payment-report-container';
import IssuerAdhocReportContainer from './components/issuer-adhoc-report-container/issuer-adhoc-report-container';
import EventBasedReportContainer from './components/event-based-report-container/event-based-report-container';
import { useHistory } from 'react-router-dom';
import ReportsService from '../../../../services/reports-service';

import './ca-maker-reports-module.scss';

// Field definitions for dynamic Filter
const corpFields = [
  { key: 'status',      label: 'Status',       type: 'checkbox' },
  { key: 'paymentDate', label: 'Payment Date', type: 'date'     },
  { key: 'issuer',      label: 'Issuer',       type: 'checkbox' }
];

const eventFields = [
  { key: 'reportTitle', label: 'Report Name', type: 'checkbox' },
  { key: 'securityIssuer',     label: 'Issuer',      type: 'checkbox' },
  { key: 'isin',       label: 'ISIN',        type: 'checkbox' },
  { key: 'status',     label: 'Status',      type: 'checkbox' },
  { key: 'dateSubmitted', label: 'Date Submitted', type: 'date' }
];

const issuerFields = [
  { key: 'taxAdjustmentType',    label: 'Report Name',    type: 'checkbox' },
  { key: 'securityIssuer',        label: 'Issuer',         type: 'checkbox' },
  { key: 'bpId',          label: 'BPID',           type: 'checkbox'     },
  { key: 'securityName',  label: 'Security Name',  type: 'checkbox'     },
  { key: 'isin',          label: 'ISIN',           type: 'checkbox' },
  { key: 'issuerRequestDate', label: 'Requested Date', type: 'date'     },
  { key: 'status',        label: 'Status',         type: 'checkbox' }
];

const CaMakerReportsModule = () => {
  const [activeTab, setActiveTab] = useState('corporate');
  const [loading, setLoading] = useState(true);

  const [interestItems, setInterestItems] = useState([]);
  const [filteredInterestItems, setFilteredInterestItems] = useState([]);
  const [eventItems, setEventItems] = useState([]);
  const [filteredEventItems, setFilteredEventItems] = useState([]);
  const [issuerItems, setIssuerItems] = useState([]);
  const [filteredIssuerItems, setFilteredIssuerItems] = useState([]);

  // Persisted filter state per tab
  const [corpFilter,   setCorpFilter]   = useState({ status: [], paymentDate: '', issuer: [] });
  const [eventFilter,  setEventFilter]  = useState({ reportName: [], issuer: [], isin: [], status: [] });
  const [issuerFilter, setIssuerFilter] = useState({ reportName: [], issuer: [], bpid: '', securityName: '', isin: [], requestedDate: '', status: [] });

  const reportsService = useMemo(() => new ReportsService(), []);
  const history = useHistory();

  useEffect(() => {
    if (activeTab === 'corporate') {
      setCorpFilter({ status: [], paymentDate: '', issuer: [] });
      setFilteredInterestItems(interestItems);
    } else if (activeTab === 'event') {
      setEventFilter({ reportTitle: [], issuer: [], isin: [], status: [] }); // fixed typo: reportName → reportTitle
      setFilteredEventItems(eventItems);
    } else if (activeTab === 'issuer') {
      setIssuerFilter({ taxAdjustmentType: [], issuer: [], bpId: [], securityName: [], isin: [], requestedDate: '', status: [] });
      setFilteredIssuerItems(issuerItems);
    }
  }, [activeTab, interestItems, eventItems, issuerItems]);
  

  useEffect(() => {
    const getReports = async () => {
      try {
        const result = await reportsService.getSecurities();
        setInterestItems(result.items);
        setFilteredInterestItems(result.items);
        setIssuerItems(result.item2);
        setFilteredIssuerItems(result.item2);
        setEventItems(result.item3);
        setFilteredEventItems(result.item3);
      } catch (err) {
        console.error(err);
        history.push('/internal-server-error');
      } finally {
        setLoading(false);
      }
    };
    getReports();
  }, [history, reportsService]);

  // Apply filters dynamically
  const applyFilters = useCallback((items, filters) => {
    return items.filter(item => {
      return Object.entries(filters).every(([key, val]) => {
        if (Array.isArray(val)) {
          return val.length ? val.includes(item[key]) : true;
        }
        return val ? item[key] === val : true;
      });
    });
  }, []);

  const handleFilterChange = useCallback(filters => {
    if (activeTab === 'corporate') {
      setCorpFilter(filters);
      setFilteredInterestItems(applyFilters(interestItems, filters));
    } else if (activeTab === 'event') {
      setEventFilter(filters);
      setFilteredEventItems(applyFilters(eventItems, filters));
    } else {
      setIssuerFilter(filters);
      setFilteredIssuerItems(applyFilters(issuerItems, filters));
    }
  }, [activeTab, interestItems, eventItems, issuerItems, applyFilters]);

  const getFilterSetup = () => {
    switch (activeTab) {
      case 'corporate': return { fields: corpFields,   filterState: corpFilter,   items: interestItems,   filtered: filteredInterestItems };
      case 'event':     return { fields: eventFields,  filterState: eventFilter,  items: eventItems,      filtered: filteredEventItems };
      case 'issuer':    return { fields: issuerFields, filterState: issuerFilter, items: issuerItems,      filtered: filteredIssuerItems };
      default:          return {};
    }
  };

  const { fields, filterState, items: filterItems, filtered } = getFilterSetup();

  return (
    <Container fluid className="maker-reports-module-container">
      {loading ? (
        <div className="loading-container">
          <Spinner animation="border" role="status">
            <span className="sr-only">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <>
          <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-3">
            <Tab eventKey="corporate" title="Corporate Action Payment Reports" />
            <Tab eventKey="event" title="Event-Based Reports" />
            <Tab eventKey="issuer" title="Issuer-Adhoc Reports" />
          </Tabs>
          <Row>
            <Col xs={12} md={3} className="panel">
              <Filter
                items={filterItems}
                filter={filterState}
                onFilterChange={handleFilterChange}
                fields={fields}
              />
            </Col>
            <Col xs={12} md={9}>
              {activeTab === 'corporate' && (
                <CorporateActionPaymentReportContainer items={filtered} />
              )}
              {activeTab === 'event' && (
                <EventBasedReportContainer items={filtered} />
              )}
              {activeTab === 'issuer' && (
                <IssuerAdhocReportContainer items={filtered} />
              )}
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default CaMakerReportsModule;